import React, { useState } from 'react';
import axios from 'axios';
import { createApiUrlWithPrefix } from '../config/api';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { CreditCard } from 'lucide-react';
import Modal, { ModalFooterBar } from './ui/Modal';

const PersonnelAddPaymentModal = ({ personnel, isOpen, onClose, refreshList }) => {
  const { t } = useTranslation();
  const [paymentData, setPaymentData] = useState({
    description: `Salary for ${personnel?.firstName || personnel?.name || ''}`,
    amount: '',
    type: 'Salary',
    paymentDate: new Date().toISOString().split('T')[0]
  });

  const paymentTypes = [
    { value: 'Salary', label: t('common.salary') },
    { value: 'Salary Advance', label: t('common.salaryAdvance') },
    { value: 'Bonus', label: t('common.bonus') },
    { value: 'Overtime', label: t('common.overtime') },
    { value: 'Allowance', label: t('common.allowance') },
    { value: 'Reimbursement', label: t('common.reimbursement') },
    { value: 'Other', label: t('common.other') }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        personnelId: personnel.id,
        paymentType: paymentData.type,
        description: paymentData.description,
        amount: parseFloat(paymentData.amount),
        paymentDate: new Date(paymentData.paymentDate).toISOString(),
        notes: '',
        paymentMethod: 'Bank Transfer',
        referenceNumber: ''
      };
      await axios.post(createApiUrlWithPrefix('PersonnelPayment'), payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      toast.success(t('common.paymentCreatedSuccessfully'));
      onClose();
      refreshList?.();
    } catch (error) {
      console.error('Error saving payment:', error);
      toast.error(t('common.failedToSavePayment'));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('common.addPayment')}
      subtitle={personnel?.firstName || personnel?.name || ''}
      icon={CreditCard}
      variant="purple"
      size="sm"
      bodyClassName="p-0"
      footer={
        <ModalFooterBar hint="Payment will be recorded immediately.">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
          >
            {t('common.cancelLabel')}
          </button>
          <button
            type="submit"
            form="personnel-payment-form"
            className="px-5 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            {t('common.createPayment')}
          </button>
        </ModalFooterBar>
      }
    >
      <form id="personnel-payment-form" onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('common.paymentTypeLabel')}</label>
          <select
            value={paymentData.type}
            onChange={(e) => setPaymentData({ ...paymentData, type: e.target.value })}
            className="input-field"
            required
          >
            {paymentTypes.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('common.descriptionLabel')}</label>
          <input
            type="text"
            value={paymentData.description}
            onChange={(e) => setPaymentData({ ...paymentData, description: e.target.value })}
            className="input-field"
            placeholder={t('common.descriptionPlaceholder')}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('common.amountLabel')}</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={paymentData.amount}
            onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
            className="input-field"
            placeholder={t('common.amountPlaceholder')}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('common.paymentDateLabel')}</label>
          <input
            type="date"
            value={paymentData.paymentDate}
            onChange={(e) => setPaymentData({ ...paymentData, paymentDate: e.target.value })}
            className="input-field"
            required
          />
        </div>
      </form>
    </Modal>
  );
};

export default PersonnelAddPaymentModal;
